import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { L10nConfig, L10nTranslationLoader, L10nProvider, L10N_CONFIG} from "angular-l10n";
import { Observable, shareReplay } from "rxjs";
import { Localization } from '../shared/service-clients/localization';

export enum SamplicityProviderType {
  Fallback = 0,
  Static = 1,
  WebAPI = 2,
  LocalizationMicroService = 4
}

@Injectable({ providedIn: 'root' })
export class SamplicityTranslationLoader implements L10nTranslationLoader {
  private cache: { [key: string]: Observable<any> | undefined } = {};

  constructor(
    @Inject(L10N_CONFIG) private configuration: L10nConfig,
    private readonly localizationClient: Localization,
    private readonly http: HttpClient
  ) { }

  public get(language: string, provider: L10nProvider): Observable<any> {
    const args = provider.asset as {
      type: SamplicityProviderType;
      prefix?: string;
      path?: string;
      moduleId?: string;
    };
    let request: Observable<any>;
    let cacheKey = '';

    if (args.type === SamplicityProviderType.LocalizationMicroService) {
      request = this.localizationClient.getLocalizedResourcesByModule(args.moduleId, language);
      cacheKey = `LocalizationMicroService-${args.moduleId}-${language}`;
    }
    else {
      const headers: HttpHeaders = new HttpHeaders({ 'Content-Type': 'application/json' });
      const options: any = {
        headers: headers
      };
      let url = '';
      switch (args.type) {
        case SamplicityProviderType.WebAPI:
          url += args.path + language;
          break;
        default:
          url += args.prefix + language + '.json';
      }
      request = this.http.get(url, options);
      cacheKey = url;
    }

    if (this.configuration.cache) {
      return this.caching(cacheKey, request);
    }
    return request;
  }

  private caching(key: string, request: Observable<any>): Observable<any> {
    const cached: Observable<any> | undefined = this.cache[key];
    if (cached) {
      return cached;
    }

    const cachedObservable = new Observable(subscriber => {
      request.subscribe({
        next: (value: any) => {
          if (value && value['result'] !== undefined) {
            subscriber.next(value['result']);
          } else {
            subscriber.next(value);
          }
        },
        error: (error: any) => {
          this.cache[key] = undefined;
          subscriber.error(error);
        },
        complete: () => {
          subscriber.complete();
        }
      });
    }).pipe(
      shareReplay(1)
    );

    this.cache[key] = cachedObservable;
    return cachedObservable;
  }
}